import { useEffect, useState } from 'react';
import { QuerySettingsType } from 'utils/google-maps/types';
import { useSearchOptions } from './use-search-options';
import { SearchStrategyFactory } from 'strategy/search';
import { useThemeContext, useUser } from 'contexts';
import useDebounce from './use-debounce';

import type { SearchAgentPrediction } from 'components/suggested-location-dropdown';
import type SearchPrediction from 'data/search-predictions';
import type { SearchOptions } from 'themes/themeConfig';
import type { FeaturesType } from 'contexts/features';


interface UseLocationSearchProps {
  queryString: string;
  useUsListings?: boolean;
  searchOptions?: SearchOptions;
  features?: FeaturesType;
}

/**
 * SEARCH STRATEGY PATTERN IMPLEMENTATION
 *
 * This hook implements the Strategy Pattern for handling different types of searches.
 * The strategy pattern allows us to define a family of algorithms (search strategies),
 * encapsulate each one, and make them interchangeable at runtime.
 *
 * AVAILABLE SEARCH STRATEGIES:
 *
 * 1. DefaultSearchStrategy (src/strategy/search/DefaultSearchStrategy.ts)
 *    - Handles general location searches (cities, neighborhoods, streets, provinces)
 *    - Combines results from go-search service and Google Maps API
 *    - Used when no specific search option is provided
 *
 * 3. HomeAppraisalSearchStrategy (src/strategy/search/HomeAppraisalSearchStrategy.ts)
 *    - Focuses on address-based searches for home appraisals
 *    - Prioritizes street addresses over cities/provinces
 *    - Used when searchOptions === SEARCH_OPTION_HOME_APPRAISAL
 *
 * 4. InServiceSearchStrategy (src/strategy/search/InServiceSearchStrategy.ts)
 *    - Handles searches for military in-service locations
 *    - Used when searchOptions === SEARCH_OPTION_IN_SERVICE
 *
 * 5. MilitaryBaseSearchStrategy (src/strategy/search/MilitaryBaseSearchStrategy.ts)
 *    - Handles searches specific to military bases
 *    - Used when searchOptions === SEARCH_OPTION_MILITARY_BASE
 *
 * STRATEGY SELECTION:
 * The SearchStrategyFactory (src/strategy/search/SearchStrategyFactory.ts) determines
 * which strategy to use based on the searchOptions parameter. Each strategy implements
 * the SearchStrategy interface (src/strategy/search/SearchStrategy.ts).
 *
 * BASE SEARCH STRATEGY:
 * All strategies extend or implement the base SearchStrategy interface which defines
 * the contract that all search implementations must follow. The main search method
 * returns both searchPredictions and searchAgentPredictions.
 *
 * FLOW:
 * 1. User types in search input
 * 2. searchOptions determines which strategy to use
 * 3. Strategy executes its specific search logic
 * 4. Results are returned and displayed in the UI
 */
export default function useLocationSearch({ queryString, useUsListings = false, searchOptions }: UseLocationSearchProps) {
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingSearchPredictions, setIsLoadingSearchPredictions] = useState(false);
  const [searchPredictions, setSearchPredictions] = useState<SearchPrediction[]>([]);
  const [searchAgentPredictions, setSearchAgentPredictions] = useState<SearchAgentPrediction[]>([]);
  const { siteLocation } = useUser();
  const { themeName } = useThemeContext();
  // Apply tenant-specific search options
  const effectiveSearchOptions = useSearchOptions(searchOptions);

  // Debounced search function using the Strategy Pattern implementation
  const debouncedSearch = useDebounce(() => searchWithStrategy(), 400);

  /**
   * Main search function implementing the Strategy Pattern.
   *
   * This function:
   * 1. Determines if a search should be performed (query length > 2)
   * 2. Uses SearchStrategyFactory to get the appropriate strategy
   * 3. Executes the strategy's search method with provided parameters
   * 4. Updates state with the results
   *
   * HOW THE .search() FUNCTION WORKS:
   *
   * 1. **Call `searchStrategy.search()`**:
   *    - Since all strategies extend BaseSearchStrategy, this calls `BaseSearchStrategy.search()`
   *    - The search() method is defined in the BaseSearchStrategy parent class (src/strategy/search/BaseSearchStrategy.ts)
   *
   * 2. **BaseSearchStrategy.search()**:
   *    - Calls `getSearchPredictions()` → fetches go-search predictions only
   *    - It defines abstract method `processSearchResults()` that each strategy must implement
   *    - This way when we use processSearchResults() in the strategy it has goSearchPredictions as a parameter already
   *
   * 3. **Each Strategy's processSearchResults() does**:
   *    - **DefaultSearchStrategy**: Calls Google Maps API + combines predictions
   *    - **HomeAppraisalSearchStrategy**: Calls Google Maps for addresses + combines
   *    - **MilitaryBaseSearchStrategy**: Filters for military bases + combines
   *    - **InServiceSearchStrategy**: Handles military in-service specific logic
   *
   * **Key Point**: BaseSearchStrategy.search() handles go-search fetching uniformly,
   * then each strategy's processSearchResults() customizes how it processes those
   * go-search predictions (adding Google data, filtering, transforming, etc.).
   */
  const searchWithStrategy = async () => {
    const performSearch = queryString.length > 2;
    const performReset = queryString.length === 0;

    if (performSearch) {
      setIsLoadingSearchPredictions(true);

      try {
        // Get the appropriate search strategy based on effectiveSearchOptions
        const searchStrategy = SearchStrategyFactory.getStrategy(effectiveSearchOptions);
        // Execute the search using the selected strategy
        const { searchPredictions: predictions, searchAgentPredictions: agentPredictions } =
          await searchStrategy.search({
            queryString,
            useUsListings,
            siteLocation,
            themeName,
          });

        setSearchPredictions(predictions);
        setSearchAgentPredictions(agentPredictions);
      } catch (err: any) {
        console.error(err);
        setSearchPredictions([]);
        setSearchAgentPredictions([]);
      }

      setIsTyping(false);
      setIsLoadingSearchPredictions(false);
    } else if (performReset) {
      setIsTyping(false);
      setSearchPredictions([]);
      setSearchAgentPredictions([]);
      setIsLoadingSearchPredictions(false);
    }
  };

  useEffect(() => {
    debouncedSearch();
    !!queryString.length && setIsTyping(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString, effectiveSearchOptions]);

  return { isLoadingSearchPredictions, searchPredictions, searchAgentPredictions, isTyping };
}